import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Routes } from '../../utils/Routes';
import { ArtData } from '../../utils/ArtData';
import ArtPlaceholder from '../../assets/images/ArtPlaceholder.svg';
import SearchBar from '../common/SearchBar';

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const useIntersectionObserver = (
  elementRef: React.RefObject<HTMLElement>,
  options = { threshold: 0.1, rootMargin: '50px' },
) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(element);
      }
    }, options);
    observer.observe(element);
    return () => observer.unobserve(element);
  }, [elementRef, options]);
  return isVisible;
};

const statusConfig: Record<string, { color: string; label: string }> = {
  sold: { color: 'bg-red-400', label: 'Sold' },
  available: { color: 'bg-emerald-400', label: 'Available' },
  'not for sale': { color: 'bg-sky-400', label: 'Not for sale' },
  'on hold': { color: 'bg-amber-400', label: 'On hold' },
  'inquire for price': { color: 'bg-amber-400', label: 'Inquire for price' },
};

const ArtCard = ({ art }: { art: (typeof ArtData)[0] }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(cardRef);

  const handleImageLoad = useCallback(() => setImageLoaded(true), []);
  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

  const statusDot = statusConfig[art.metadata.status ?? ''] ?? null;

  return (
    <div
      ref={cardRef}
      id={`art-${art.metadata.id}`}
      className="break-inside-avoid mb-5"
    >
      <Link to={`${Routes.Art}/${art.metadata.id}`} className="group block">
        <div className="relative overflow-hidden rounded-sm shadow-sm hover:shadow-lg transition-shadow duration-500 cursor-pointer">
          {/* Placeholder skeleton */}
          {(!imageLoaded || imageError) && (
            <img src={ArtPlaceholder} alt="" className="w-full h-auto block" />
          )}

          {/* Lazy-loaded artwork */}
          {isVisible && !imageError && (
            <img
              src={art.image}
              alt={art.metadata.title}
              className={`w-full h-auto block transition-all duration-700 group-hover:scale-[1.025] ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              } ${imageLoaded ? '' : 'absolute inset-0'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          )}

          {/* Hover overlay — title + medium revealed on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-4 pointer-events-none">
            <h2 className="text-white text-xs font-light tracking-[0.18em] uppercase leading-snug">
              {art.metadata.title || 'Untitled'}
            </h2>
            {art.metadata.medium && (
              <p className="text-white/60 text-[10px] italic mt-0.5 leading-snug">
                {art.metadata.medium}
              </p>
            )}
          </div>

          {/* Donated badge — always visible */}
          {art.metadata.status === 'donated' && (
            <span className="absolute top-3 left-3 text-[9px] uppercase tracking-widest text-white bg-black/65 px-2 py-0.5">
              Donated
            </span>
          )}

          {/* Status dot — visible on hover */}
          {statusDot && (
            <span
              title={statusDot.label}
              className={`absolute top-3 right-3 w-2 h-2 rounded-full ${statusDot.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
            />
          )}
        </div>
      </Link>
    </div>
  );
};

export default function UnspokenArt() {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const sortedArt = useMemo(() => {
    const filtered = ArtData.filter((art) => {
      if (!debouncedSearchQuery) return true;
      const q = debouncedSearchQuery.toLowerCase();
      return (
        art.metadata.title.toLowerCase().includes(q) ||
        art.metadata.artist?.toLowerCase().includes(q)
      );
    });

    const unique = filtered.filter(
      (art, i, self) =>
        i === self.findIndex((x) => x.metadata.id === art.metadata.id),
    );

    return unique.sort((a, b) => {
      const aNum = parseInt(a.metadata.id);
      const bNum = parseInt(b.metadata.id);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.metadata.id.localeCompare(b.metadata.id);
    });
  }, [debouncedSearchQuery]);

  const location = useLocation();

  useEffect(() => {
    const artId = new URLSearchParams(location.search).get('id');
    if (artId) {
      requestAnimationFrame(() => {
        document
          .getElementById(`art-${artId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.search]);

  const handleSearch = useCallback(
    (query: string) => setSearchQuery(query),
    [],
  );

  return (
    <div className="container mx-auto mb-12 px-4 pt-4">
      <div className="mb-8">
        <SearchBar query={searchQuery} onSearch={handleSearch} />
      </div>

      {sortedArt.length > 0 ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5">
          {sortedArt.map((art) => (
            <ArtCard key={art.metadata.id} art={art} />
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-400 mt-16 tracking-widest uppercase text-xs">
          No results found for &ldquo;{debouncedSearchQuery}&rdquo;
        </div>
      )}
    </div>
  );
}
